/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { UploadCloudIcon } from './icons';

interface StartScreenProps {
  onStartTryOn: (userFile: File, garmentFile: File) => void;
  onRequireAuth: () => void;
  isLoadingStats?: boolean;
}

const StartScreen: React.FC<StartScreenProps> = ({ onStartTryOn, onRequireAuth, isLoadingStats }) => {
  const { user } = useAuth();
  const [userFile, setUserFile] = useState<File | null>(null);
  const [garmentFile, setGarmentFile] = useState<File | null>(null);
  const [userPreview, setUserPreview] = useState<string | null>(null);
  const [garmentPreview, setGarmentPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = useCallback((file: File, type: 'user' | 'garment') => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    if (!user) {
      onRequireAuth();
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (type === 'user') {
        setUserFile(file);
        setUserPreview(dataUrl);
      } else {
        setGarmentFile(file);
        setGarmentPreview(dataUrl);
      }
      setError(null);
    };
    reader.readAsDataURL(file);
  }, [user, onRequireAuth]);

  const handleStart = () => {
    if (userFile && garmentFile) {
      onStartTryOn(userFile, garmentFile);
    } else {
      setError('Please upload both images to proceed.');
    }
  };

  const removeImage = (type: 'user' | 'garment') => {
    if (type === 'user') {
      setUserFile(null);
      setUserPreview(null);
    } else {
      setGarmentFile(null);
      setGarmentPreview(null);
    }
  };

  const screenVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <motion.div
      key="uploader"
      className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center gap-8"
      variants={screenVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <div className="text-center max-w-2xl mb-4">
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 leading-tight">
          Virtual Fitting Room
        </h1>
        <p className="mt-4 text-lg text-gray-600">
          Upload your photo and a photo of the clothing item to see the result instantly.
        </p>
      </div>

      <div className="w-full grid md:grid-cols-2 gap-6">
        {/* User Photo Upload */}
        <div className="flex flex-col gap-3">
          <h3 className="font-semibold text-gray-700 text-center">1. Your Photo</h3>
          {!userPreview ? (
            <label className="flex-1 aspect-[3/4] flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors bg-white">
              <UploadCloudIcon className="w-10 h-10 text-gray-400 mb-2" />
              <span className="text-sm font-medium text-gray-600">Tap to Upload</span>
              <span className="text-xs text-gray-400 mt-1">Full body photo preferred</span>
              <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'user')} />
            </label>
          ) : (
            <div className="relative flex-1 aspect-[3/4] rounded-xl overflow-hidden border border-gray-200 bg-gray-100 group">
              <img src={userPreview} alt="User" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage('user')}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Only one image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
          )}
        </div>

        {/* Garment Photo Upload */}
        <div className="flex flex-col gap-3">
          <h3 className="font-semibold text-gray-700 text-center">2. Clothing Photo</h3>
          {!garmentPreview ? (
            <label className="flex-1 aspect-[3/4] flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors bg-white">
              <UploadCloudIcon className="w-10 h-10 text-gray-400 mb-2" />
              <span className="text-sm font-medium text-gray-600">Tap to Upload</span>
              <span className="text-xs text-gray-400 mt-1">Clear photo of the item</span>
              <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'garment')} />
            </label>
          ) : (
            <div className="relative flex-1 aspect-[3/4] rounded-xl overflow-hidden border border-gray-200 bg-gray-100 group">
              <img src={garmentPreview} alt="Garment" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage('garment')}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-red-500 font-medium">{error}</p>}

      <button
        onClick={handleStart}
        disabled={!userFile || !garmentFile || isLoadingStats}
        className="mt-4 px-10 py-3 bg-gray-900 text-white text-lg font-semibold rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center gap-3"
      >
        {isLoadingStats ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>جاري التحقق من الرصيد...</span>
          </>
        ) : (
          'Start Try-On Now'
        )}
      </button>
    </motion.div>
  );
};

export default StartScreen;
